import { CSSProperties, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ImageArrayValue, YOUTUBE_URL_REGEX } from '@makyoapp/makyo-common'
import { Plus16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, SimpleInput, Tabs } from '@makyoapp/makyo-ui'

import { ImageManagerKind, useImageManagerContext } from '@/context/ImageManagerContext'
import { THUMB_SRC_M } from '@/shared/constants'

export function ImageUrlPanel() {
  const { multiple } = useImageManagerContext()

  return (
    <Tabs.Panel data-panel-id={ImageManagerKind.ImageUrl} eventKey={ImageManagerKind.ImageUrl}>
      {multiple ? <ImageUrlMultiple /> : <ImageUrl />}
    </Tabs.Panel>
  )
}

export function ImageUrl() {
  const imageManagerContextData = useImageManagerContext()
  const { onChange, onHide, id, maxHeight, forceUrlPanelHeight, multiple } = imageManagerContextData
  const [value, setValue] = useState('')
  const { t } = useTranslation()
  const [previewUrl, setPreviewUrl] = useState('')

  useEffect(() => {
    if (value) {
      const isYoutube = value?.match(YOUTUBE_URL_REGEX)
      if (isYoutube && isYoutube[2].length === 11) {
        setPreviewUrl(`https://img.youtube.com/vi/${isYoutube[2]}/0.jpg`)
      } else {
        setPreviewUrl(value)
      }
    }
  }, [value])

  useEffect(() => {
    setValue(imageManagerContextData?.value?.url)
  }, [])

  const previewStyle: CSSProperties = {
    height: forceUrlPanelHeight ? (maxHeight ? maxHeight : multiple ? '50vh' : '60vh') : undefined,
  }

  return (
    <div className='flex flex-col justify-between'>
      {previewUrl && (
        <div className='mk-im-img-preview overflow-auto' style={previewStyle}>
          <img
            alt={t('Preview')}
            loading='lazy'
            //className='max-h-[60%]!'
            className='thumbnail'
            src={previewUrl || THUMB_SRC_M}
          />
        </div>
      )}
      {forceUrlPanelHeight && !previewUrl && (
        <div className='mk-im-img-preview overflow-auto bg-[#eee]' style={previewStyle}>
          <img src={THUMB_SRC_M} loading='lazy' /*className='max-h-[60%]!'*/ />
        </div>
      )}
      <div className='flex-c4'>
        <form
          className='w-full'
          onSubmit={(e) => {
            e.preventDefault()
            if (previewUrl) {
              onChange(ImageManagerKind.ImageUrl, previewUrl)
            }
          }}
        >
          <SimpleInput
            autoFocus
            id={id ? `${id}-url` : undefined}
            inputClassName='mk-im-url-input'
            showClear={Boolean(previewUrl)}
            placeholder={t('Enter Image URL')}
            value={value}
            onChange={(e) => {
              setValue(e.currentTarget.value)
            }}
            onClear={() => {
              setValue('')
              setPreviewUrl('')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation()
              }
            }}
          />
        </form>
        <Button
          disabled={!previewUrl}
          onClick={() => {
            onChange(ImageManagerKind.ImageUrl, previewUrl)
            setValue('')
            onHide?.()
          }}
        >
          <Trans>Use Image</Trans>
        </Button>
      </div>
    </div>
  )
}

export function ImageUrlMultiple() {
  const { id, onChange, onHide, selectedImages, setSelectedImages } = useImageManagerContext()
  const [hasInteracted, setHasInteracted] = useState(false)

  const handleAddImage = () => {
    setSelectedImages([
      ...selectedImages,
      {
        Url: '',
        Description: '',
        Size: 0,
        Owner: '',
        Title: '',
        CreatedAt: new Date(),
        Format: '',
        Height: 0,
        Tags: [],
        Width: 0,
      },
    ])
    setHasInteracted(true) // Ensure CTA remain visible after adding
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>, i: number) => {
    const copy = [...selectedImages]
    copy[i] = {
      Url: e.currentTarget.value,
      CreatedAt: new Date(),
      Format: '',
      Size: 0,
      Tags: [],
      Title: '',
      Height: 0,
      Width: 0,
      Owner: '',
      Description: '',
    } as ImageArrayValue
    setSelectedImages(copy)
    setHasInteracted(true) // Update interaction state
  }

  const handleRemoveImage = (i: number) => {
    const copy = [...selectedImages]
    copy.splice(i, 1)
    setSelectedImages(copy)
    setHasInteracted(true) // Ensure CTA remain visible after removing
  }

  const handleClear = () => {
    setSelectedImages([]) // Clear images on cancel
    setHasInteracted(true) // Ensure CTA remain visible
  }

  // track initial selected images
  const initialSelectedRef = useRef(selectedImages)

  return (
    <>
      <div className='flex flex-col gap-2 pb-4'>
        {selectedImages.map((item, i) => {
          return (
            <div className='flex-c' key={i}>
              {item.Url !== '' && (
                <div className='w-[36px] h-[36px] overflow-hidden rounded-sm'>
                  <img src={item.Url} className='object-contain' />
                </div>
              )}
              <SimpleInput
                autoFocus
                id={id ? `${id}-url` : undefined}
                inputClassName='mk-im-url-input'
                value={item.Url}
                onChange={(e) => handleUrlChange(e, i)}
                onClear={() => handleRemoveImage(i)}
              />
              {selectedImages.length > 0 && (
                <Button
                  variant={ButtonVariant.Ghost}
                  squared
                  noPadding
                  onClick={() => handleRemoveImage(i)}
                  icon={<Trash16 />}
                />
              )}
              {i === selectedImages.length - 1 ? (
                <Button onClick={handleAddImage} noPadding squared icon={<Plus16 />} />
              ) : (
                <div className='w-9 h-9' />
              )}
            </div>
          )
        })}

        {selectedImages.length === 0 && (
          <div className='flex-c'>
            <SimpleInput
              id={id ? `${id}-url` : undefined}
              inputClassName='mk-im-url-input'
              placeholder='Enter Image URL'
              onChange={(e) => {
                const value = e.currentTarget.value
                if (value) {
                  setSelectedImages([
                    {
                      Url: value,
                      Description: '',
                      Size: 0,
                      Owner: '',
                      Title: '',
                      CreatedAt: new Date(),
                      Format: '',
                      Height: 0,
                      Tags: [],
                      Width: 0,
                    },
                  ])
                  setHasInteracted(true)
                }
              }}
            />
            <Button onClick={handleAddImage} noPadding squared icon={<Plus16 />} disabled />
          </div>
        )}
      </div>
      {hasInteracted || selectedImages.length > 0 ? (
        <div className='flex-c4'>
          {/* TODO: Find proper way to close modal */}
          <Button
            variant={ButtonVariant.Outlined}
            onClick={() => {
              onChange(ImageManagerKind.MyImages, initialSelectedRef.current)
              onHide?.()
            }}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button variant={ButtonVariant.Outlined} onClick={handleClear} className='w-20 mr-auto'>
            <Trans>Clear</Trans>
          </Button>
          <Button
            className='w-20'
            onClick={() => {
              onChange(
                ImageManagerKind.ImageUrl,
                selectedImages.filter((i) => Boolean(i.Url))
                /*.map((v) => {
                  return {
                    Url: v,
                    Description: '',
                    Title: '',
                    CreatedAt: new Date(),
                    Format: '',
                    Height: 0,
                    Width: 0,
                    Owner: '',
                    Size: 0,
                    Tags: [],
                  } as ImageArrayValue
                })*/
              )
              setSelectedImages([
                {
                  Url: '',
                  Description: '',
                  Title: '',
                  CreatedAt: new Date(),
                  Format: '',
                  Height: 0,
                  Width: 0,
                  Owner: '',
                  Size: 0,
                  Tags: [],
                },
              ])
              onHide?.()
            }}
          >
            <Trans>Save</Trans>
          </Button>
        </div>
      ) : null}
    </>
  )
}
